import { useRef, useEffect, useCallback, useMemo } from "react";
import LogoBubble from "./LogoBubble";
import { useClients, useClientOrder } from "../../data/clients";
import { urlFor } from "../../data/sanity/client";

const getBaseSpeed = () => window.innerWidth < 640 ? -1.7 : -2.0;

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

function LogoMarquee({ darkMode }) {
  const allClients = useClients();
  const clientOrder = useClientOrder();
  const containerRef = useRef(null);

  // Order comes from the drag-sorted list on the home page; anything missing
  // from it keeps its alphabetical place at the end. Memoised because the
  // preload effect below depends on this array's identity.
  const clients = useMemo(() => {
    const rank = (c) => {
      const i = clientOrder.indexOf(c._id);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...allClients].sort((a, b) => rank(a) - rank(b));
  }, [allClients, clientOrder]);
  const trackRef     = useRef(null);
  const offsetRef    = useRef(0);
  const velocityRef  = useRef(getBaseSpeed());
  const isDragging   = useRef(false);
  const isHovering   = useRef(false);
  const lastX        = useRef(0);
  const lastTime     = useRef(0);
  const rafRef       = useRef(null);

  // Preload both theme variants so src swaps are instant with no layout shift
  useEffect(() => {
    clients.forEach((c) => {
      [c.logoDark, c.logoLight].filter(Boolean).forEach((source) => {
        const img = new window.Image();
        img.src = urlFor(source).height(120).url();
      });
    });
  }, [clients]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = () => {
      if (!isDragging.current) {
        const target = isHovering.current ? 0 : getBaseSpeed();
        velocityRef.current += (target - velocityRef.current) * 0.06;
        offsetRef.current += velocityRef.current;
      }

      const half = track.scrollWidth / 2;
      if (offsetRef.current < -half) offsetRef.current += half;
      if (offsetRef.current > 0)     offsetRef.current -= half;

      track.style.transform = `translateX(${offsetRef.current}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const onMouseEnter = useCallback((e) => { isHovering.current = !e.target.closest("button"); }, []);
  const onMouseLeave = useCallback(() => { isHovering.current = false; isDragging.current = false; }, []);

  const onMouseDown = useCallback((e) => {
    isDragging.current = true;
    lastX.current = e.clientX;
    lastTime.current = performance.now();
    velocityRef.current = 0;
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback((e) => {
    isHovering.current = !e.target.closest("button");
    if (!isDragging.current) return;
    const dx  = e.clientX - lastX.current;
    const now = performance.now();
    const dt  = now - lastTime.current;

    offsetRef.current += dx;

    if (dt > 0) {
      const v = (dx / dt) * 16;
      velocityRef.current = Math.max(-10, Math.min(10, v));
    }

    lastX.current  = e.clientX;
    lastTime.current = now;
  }, []);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  const scroll = useCallback((dir) => { velocityRef.current = dir * 12; }, []);

  // Touch support (passive: false needed to allow preventDefault for horizontal drag)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startY = 0;
    let isHorizontal = null;

    const onTouchStart = (e) => {
      if (e.target.closest("button")) return;
      lastX.current    = e.touches[0].clientX;
      startY           = e.touches[0].clientY;
      lastTime.current = performance.now();
      velocityRef.current = 0;
      isDragging.current  = true;
      isHorizontal        = null;
    };

    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.touches[0].clientX - lastX.current;
      const dy = e.touches[0].clientY - startY;

      if (isHorizontal === null) isHorizontal = Math.abs(dx) > Math.abs(dy);
      if (!isHorizontal) { isDragging.current = false; return; }

      e.preventDefault();
      const now = performance.now();
      const dt  = now - lastTime.current;
      offsetRef.current += dx;
      if (dt > 0) velocityRef.current = Math.max(-10, Math.min(10, (dx / dt) * 16));
      lastX.current    = e.touches[0].clientX;
      lastTime.current = now;
    };

    const onTouchEnd = () => { isDragging.current = false; isHorizontal = null; };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove",  onTouchMove,  { passive: false });
    container.addEventListener("touchend",   onTouchEnd,   { passive: true });

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove",  onTouchMove);
      container.removeEventListener("touchend",   onTouchEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {/* Left edge — gradient + button */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center pr-6 sm:pr-10 bg-gradient-to-r from-white dark:from-black to-transparent pointer-events-none">
        <button
          onClick={() => scroll(1)}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Scroll logos left"
          className="pointer-events-auto flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border border-border-light dark:border-border-dark bg-white dark:bg-black text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
        >
          <ChevronLeft />
        </button>
      </div>

      {/* Right edge — gradient + button */}
      <div className="absolute right-0 top-0 bottom-0 z-20 flex items-center pl-6 sm:pl-10 bg-gradient-to-l from-white dark:from-black to-transparent pointer-events-none">
        <button
          onClick={() => scroll(-1)}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Scroll logos right"
          className="pointer-events-auto flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border border-border-light dark:border-border-dark bg-white dark:bg-black text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
        >
          <ChevronRight />
        </button>
      </div>

      <div ref={trackRef} className="flex gap-8 sm:gap-16 w-max py-2">
        {[...clients, ...clients].map((c, i) => (
          <LogoBubble key={`${c._id}-${i}`} client={c} darkMode={darkMode} />
        ))}
      </div>
    </div>
  );
}

export default LogoMarquee;
